import Vue from "vue";
import { Action, Module, VuexModule, Mutation } from "vuex-module-decorators";

import { Dictionary } from "@/lib/Dictionary.type";
import dataStore from "@/store";

export const name = "UiPersisterStore";

const getDefaultState = () => {
  return {
    cutoffReportFactoriesIds: [],
    cutoffReportHolidayPeriodId: undefined,
    cutoffReportProductsIds: [],
    plushieReviewComment: {},
    plushieRejectComment: {},
    plushieRejectionReasonIds: {},
    plushieMessage: {},
    productionTimeAlertDaysCount: 7,
    lastBulkShipmentBoxNumber: undefined,
    lastBulkShipmentDistributionCenter: undefined,
    lastBulkShipmentCarrier: undefined,
  };
};

@Module({ name })
export class UiPersisterStore extends VuexModule {
  cutoffReportFactoriesIds: string[] = [];
  cutoffReportHolidayPeriodId: string | undefined = undefined;
  cutoffReportProductsIds: string[] = [];
  plushieReviewComment: Dictionary<string> = {};
  plushieRejectComment: Dictionary<string> = {};
  plushieRejectionReasonIds: Dictionary<string[]> = {};
  plushieMessage: Dictionary<string> = {};
  productionTimeAlertDaysCount = 7;
  lastBulkShipmentBoxNumber: number | undefined = undefined;
  lastBulkShipmentDistributionCenter: string | undefined = undefined;
  lastBulkShipmentCarrier: string | undefined = undefined;

  // ################################### PLUSHIE REVIEW COMMENT #########################################

  get getPlushieReviewComment(): (id: string) => string | undefined {
    return (id: string) => {
      return this.plushieReviewComment[id];
    };
  }

  @Mutation
  updatePlushieReviewComment({
    plushieId,
    comment,
  }: {
    plushieId: string;
    comment: string;
  }): void {
    Vue.set(this.plushieReviewComment, plushieId, comment);
  }

  @Mutation
  deletePlushieReviewComment({ plushieId }: { plushieId: string }): void {
    Vue.delete(this.plushieReviewComment, plushieId);
  }

  // ################################### PLUSHIE REJECT COMMENT #########################################

  get getPlushieRejectComment(): (id: string) => string | undefined {
    return (id: string) => {
      return this.plushieRejectComment[id];
    };
  }

  @Mutation
  updatePlushieRejectComment({
    plushieId,
    comment,
  }: {
    plushieId: string;
    comment: string;
  }): void {
    Vue.set(this.plushieRejectComment, plushieId, comment);
  }

  @Mutation
  deletePlushieRejectComment({ plushieId }: { plushieId: string }): void {
    Vue.delete(this.plushieRejectComment, plushieId);
  }

  // ################################### PLUSHIE REJECTION REASON IDS #########################################

  get getPlushieRejectionReasonIds(): (id: string) => string[] | undefined {
    return (id: string) => {
      return this.plushieRejectionReasonIds[id];
    };
  }

  @Mutation
  updatePlushieRejectionReasonIds({
    plushieId,
    ids,
  }: {
    plushieId: string;
    ids: string[];
  }): void {
    Vue.set(this.plushieRejectionReasonIds, plushieId, ids);
  }

  @Mutation
  deletePlushieRejectionReasonIds({ plushieId }: { plushieId: string }): void {
    Vue.delete(this.plushieRejectionReasonIds, plushieId);
  }

  // ################################### PLUSHIE MESSAGE #########################################

  get getPlushieMessage(): (id: string) => string | undefined {
    return (id: string) => {
      return this.plushieMessage[id];
    };
  }

  @Mutation
  updatePlushieMessage({
    plushieId,
    message,
  }: {
    plushieId: string;
    message: string;
  }): void {
    Vue.set(this.plushieMessage, plushieId, message);
  }

  @Mutation
  deletePlushieMessage({ plushieId }: { plushieId: string }): void {
    Vue.delete(this.plushieMessage, plushieId);
  }

  // ################################### Production Time Alert Report #########################################

  @Mutation
  updateProductionTimeAlertDaysCount(daysCount: number): void {
    this.productionTimeAlertDaysCount = daysCount;
  }

  // ################################### Cutoff Report #########################################

  get getCutoffReportFactoriesIds(): string[] {
    return this.cutoffReportFactoriesIds;
  }

  get getCutoffReportHolidayPeriodId(): string | undefined {
    return this.cutoffReportHolidayPeriodId;
  }

  get getCutoffReportProductsIds(): string[] {
    return this.cutoffReportProductsIds;
  }

  @Mutation
  updateCutoffReportFactoriesIds(ids: string[]): void {
    this.cutoffReportFactoriesIds = ids;
  }

  @Mutation
  updateCutoffReportHolidayPeriodId(id: string | undefined): void {
    Vue.set(this, "cutoffReportHolidayPeriodId", id);
  }

  @Mutation
  updateCutoffReportProductsIds(ids: string[]): void {
    this.cutoffReportProductsIds = ids;
  }

  // ################################### BULK SHIPMENT PERSISTENCE #########################################

  get getLastBulkShipmentBoxNumber(): number | undefined {
    return this.lastBulkShipmentBoxNumber;
  }

  get getLastBulkShipmentDistributionCenter(): string | undefined {
    return this.lastBulkShipmentDistributionCenter;
  }

  get getLastBulkShipmentCarrier(): string | undefined {
    return this.lastBulkShipmentCarrier;
  }

  @Mutation
  updateLastBulkShipmentBoxNumber(boxNumber: number | undefined): void {
    Vue.set(this, "lastBulkShipmentBoxNumber", boxNumber);
  }

  @Mutation
  updateLastBulkShipmentDistributionCenter(
    distributionCenter: string | undefined
  ): void {
    Vue.set(this, "lastBulkShipmentDistributionCenter", distributionCenter);
  }

  @Mutation
  updateLastBulkShipmentCarrier(carrier: string | undefined): void {
    Vue.set(this, "lastBulkShipmentCarrier", carrier);
  }

  @Mutation
  clearLastBulkShipmentBoxNumber(): void {
    Vue.set(this, "lastBulkShipmentBoxNumber", undefined);
  }

  // ################################### EVENTS #########################################

  @Action({ rawError: true })
  async onShippingManifestCreated(): Promise<void> {
    this.clearLastBulkShipmentBoxNumber();
  }

  // ################################### DATA WIPING #########################################

  @Mutation
  resetState(): void {
    const state = (dataStore.state as any)[name];

    if (state) {
      Object.assign(state, getDefaultState());
    }
  }
}
